/**
 * Timezone Utilities
 *
 * Comprehensive list of IANA timezone identifiers with UTC offsets.
 * This replaces any database queries to pg_timezone_names for better performance.
 */

export interface TimezoneOption {
  value: string; // IANA timezone identifier (e.g., 'Europe/Copenhagen')
  label: string; // Display label with UTC offset (e.g., '(UTC+1) Copenhagen')
  region: string; // Geographic region for grouping
  offset: string; // UTC offset (e.g., '+01:00')
}

/**
 * Comprehensive list of timezones organized by region
 * Based on IANA timezone database
 */
export const TIMEZONES: readonly TimezoneOption[] = [
  // Europe
  { value: 'Europe/Copenhagen', label: '(UTC+1) Copenhagen', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/London', label: '(UTC+0) London', region: 'Europe', offset: '+00:00' },
  { value: 'Europe/Paris', label: '(UTC+1) Paris', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Berlin', label: '(UTC+1) Berlin', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Rome', label: '(UTC+1) Rome', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Madrid', label: '(UTC+1) Madrid', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: '(UTC+1) Stockholm', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Oslo', label: '(UTC+1) Oslo', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Amsterdam', label: '(UTC+1) Amsterdam', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Brussels', label: '(UTC+1) Brussels', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Zurich', label: '(UTC+1) Zurich', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Vienna', label: '(UTC+1) Vienna', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Prague', label: '(UTC+1) Prague', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Warsaw', label: '(UTC+1) Warsaw', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Budapest', label: '(UTC+1) Budapest', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Athens', label: '(UTC+2) Athens', region: 'Europe', offset: '+02:00' },
  { value: 'Europe/Helsinki', label: '(UTC+2) Helsinki', region: 'Europe', offset: '+02:00' },
  { value: 'Europe/Istanbul', label: '(UTC+3) Istanbul', region: 'Europe', offset: '+03:00' },
  { value: 'Europe/Moscow', label: '(UTC+3) Moscow', region: 'Europe', offset: '+03:00' },
  { value: 'Europe/Dublin', label: '(UTC+0) Dublin', region: 'Europe', offset: '+00:00' },
  { value: 'Europe/Lisbon', label: '(UTC+0) Lisbon', region: 'Europe', offset: '+00:00' },

  // North America
  {
    value: 'America/New_York',
    label: '(UTC-5) New York',
    region: 'North America',
    offset: '-05:00',
  },
  { value: 'America/Chicago', label: '(UTC-6) Chicago', region: 'North America', offset: '-06:00' },
  {
    value: 'America/Denver',
    label: '(UTC-7) Denver',
    region: 'North America',
    offset: '-07:00',
  },
  {
    value: 'America/Los_Angeles',
    label: '(UTC-8) Los Angeles',
    region: 'North America',
    offset: '-08:00',
  },
  {
    value: 'America/Phoenix',
    label: '(UTC-7) Phoenix',
    region: 'North America',
    offset: '-07:00',
  },
  {
    value: 'America/Anchorage',
    label: '(UTC-9) Anchorage',
    region: 'North America',
    offset: '-09:00',
  },
  {
    value: 'America/Toronto',
    label: '(UTC-5) Toronto',
    region: 'North America',
    offset: '-05:00',
  },
  {
    value: 'America/Vancouver',
    label: '(UTC-8) Vancouver',
    region: 'North America',
    offset: '-08:00',
  },
  {
    value: 'America/Mexico_City',
    label: '(UTC-6) Mexico City',
    region: 'North America',
    offset: '-06:00',
  },

  // South America
  {
    value: 'America/Sao_Paulo',
    label: '(UTC-3) São Paulo',
    region: 'South America',
    offset: '-03:00',
  },
  {
    value: 'America/Buenos_Aires',
    label: '(UTC-3) Buenos Aires',
    region: 'South America',
    offset: '-03:00',
  },
  { value: 'America/Lima', label: '(UTC-5) Lima', region: 'South America', offset: '-05:00' },
  { value: 'America/Bogota', label: '(UTC-5) Bogotá', region: 'South America', offset: '-05:00' },
  {
    value: 'America/Santiago',
    label: '(UTC-4) Santiago',
    region: 'South America',
    offset: '-04:00',
  },

  // Asia
  { value: 'Asia/Tokyo', label: '(UTC+9) Tokyo', region: 'Asia', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: '(UTC+8) Shanghai', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: '(UTC+8) Hong Kong', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Singapore', label: '(UTC+8) Singapore', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Seoul', label: '(UTC+9) Seoul', region: 'Asia', offset: '+09:00' },
  { value: 'Asia/Dubai', label: '(UTC+4) Dubai', region: 'Asia', offset: '+04:00' },
  { value: 'Asia/Kolkata', label: '(UTC+5:30) Kolkata', region: 'Asia', offset: '+05:30' },
  { value: 'Asia/Bangkok', label: '(UTC+7) Bangkok', region: 'Asia', offset: '+07:00' },
  { value: 'Asia/Jakarta', label: '(UTC+7) Jakarta', region: 'Asia', offset: '+07:00' },
  { value: 'Asia/Manila', label: '(UTC+8) Manila', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Taipei', label: '(UTC+8) Taipei', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Kuala_Lumpur', label: '(UTC+8) Kuala Lumpur', region: 'Asia', offset: '+08:00' },
  { value: 'Asia/Tel_Aviv', label: '(UTC+2) Tel Aviv', region: 'Asia', offset: '+02:00' },

  // Australia & Pacific
  { value: 'Australia/Sydney', label: '(UTC+11) Sydney', region: 'Australia', offset: '+11:00' },
  {
    value: 'Australia/Melbourne',
    label: '(UTC+11) Melbourne',
    region: 'Australia',
    offset: '+11:00',
  },
  {
    value: 'Australia/Brisbane',
    label: '(UTC+10) Brisbane',
    region: 'Australia',
    offset: '+10:00',
  },
  { value: 'Australia/Perth', label: '(UTC+8) Perth', region: 'Australia', offset: '+08:00' },
  {
    value: 'Pacific/Auckland',
    label: '(UTC+13) Auckland',
    region: 'Pacific',
    offset: '+13:00',
  },
  { value: 'Pacific/Fiji', label: '(UTC+12) Fiji', region: 'Pacific', offset: '+12:00' },
  { value: 'Pacific/Honolulu', label: '(UTC-10) Honolulu', region: 'Pacific', offset: '-10:00' },

  // Africa
  { value: 'Africa/Cairo', label: '(UTC+2) Cairo', region: 'Africa', offset: '+02:00' },
  {
    value: 'Africa/Johannesburg',
    label: '(UTC+2) Johannesburg',
    region: 'Africa',
    offset: '+02:00',
  },
  { value: 'Africa/Lagos', label: '(UTC+1) Lagos', region: 'Africa', offset: '+01:00' },
  { value: 'Africa/Nairobi', label: '(UTC+3) Nairobi', region: 'Africa', offset: '+03:00' },
  { value: 'Africa/Casablanca', label: '(UTC+1) Casablanca', region: 'Africa', offset: '+01:00' },

  // Atlantic
  { value: 'Atlantic/Reykjavik', label: '(UTC+0) Reykjavik', region: 'Atlantic', offset: '+00:00' },
  { value: 'Atlantic/Azores', label: '(UTC-1) Azores', region: 'Atlantic', offset: '-01:00' },

  // UTC
  { value: 'UTC', label: '(UTC+0) Coordinated Universal Time', region: 'UTC', offset: '+00:00' },
] as const;

/**
 * Get timezone options for Select component
 * Returns array of {value, label} for dropdown
 */
export function getTimezoneOptions(): Array<{ value: string; label: string }> {
  return TIMEZONES.map(({ value, label }) => ({ value, label }));
}

/**
 * Get timezones grouped by region
 */
export function getTimezonesByRegion(): Record<string, TimezoneOption[]> {
  const grouped: Record<string, TimezoneOption[]> = {};

  for (const timezone of TIMEZONES) {
    if (!grouped[timezone.region]) {
      grouped[timezone.region] = [];
    }
    grouped[timezone.region].push(timezone);
  }

  return grouped;
}

/**
 * Find timezone by IANA identifier
 */
export function findTimezone(value: string): TimezoneOption | undefined {
  return TIMEZONES.find((tz) => tz.value === value);
}

/**
 * Validate if a string is a valid IANA timezone identifier (from our list)
 */
export function isValidTimezone(value: string): boolean {
  return TIMEZONES.some((tz) => tz.value === value);
}

/**
 * Get default timezone (Europe/Copenhagen)
 */
export const DEFAULT_TIMEZONE = 'Europe/Copenhagen';

/**
 * Common timezones for quick access (European focus)
 */
export const COMMON_TIMEZONES: readonly TimezoneOption[] = [
  { value: 'Europe/Copenhagen', label: '(UTC+1) Copenhagen', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/London', label: '(UTC+0) London', region: 'Europe', offset: '+00:00' },
  { value: 'Europe/Paris', label: '(UTC+1) Paris', region: 'Europe', offset: '+01:00' },
  { value: 'Europe/Berlin', label: '(UTC+1) Berlin', region: 'Europe', offset: '+01:00' },
  {
    value: 'America/New_York',
    label: '(UTC-5) New York',
    region: 'North America',
    offset: '-05:00',
  },
  {
    value: 'America/Los_Angeles',
    label: '(UTC-8) Los Angeles',
    region: 'North America',
    offset: '-08:00',
  },
  { value: 'Asia/Tokyo', label: '(UTC+9) Tokyo', region: 'Asia', offset: '+09:00' },
  { value: 'Australia/Sydney', label: '(UTC+11) Sydney', region: 'Australia', offset: '+11:00' },
] as const;
